import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { LanguageService } from './language.service';

export interface GlobalSettings {
  countryCode: string;
  languageCode: string;
  currencyCode: string;
}

const STORAGE_KEY = 'amplify_global_settings';

const DEFAULT_SETTINGS: GlobalSettings = {
  countryCode: 'DE',
  languageCode: 'en',
  currencyCode: 'EUR'
};

@Injectable({
  providedIn: 'root'
})
export class GlobalSettingsService {
  private readonly languageService = inject(LanguageService);

  private readonly settingsSubject: BehaviorSubject<GlobalSettings>;
  readonly settings$: Observable<GlobalSettings>;

  constructor() {
    const initial = this.loadFromStorage();
    this.settingsSubject = new BehaviorSubject<GlobalSettings>(initial);
    this.settings$ = this.settingsSubject.asObservable();
    // Apply persisted language on startup
    this.languageService.setLanguage(initial.languageCode);
  }

  get(): GlobalSettings {
    return this.settingsSubject.value;
  }

  save(settings: GlobalSettings): void {
    const validated = this.validate(settings);
    this.persist(validated);
    this.settingsSubject.next(validated);
    this.languageService.setLanguage(validated.languageCode);
  }

  private loadFromStorage(): GlobalSettings {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as Partial<GlobalSettings>;
        return this.validate({ ...DEFAULT_SETTINGS, ...parsed });
      }
    } catch {
      // fall through to defaults
    }
    // Migration: pick up language already saved by LanguageService
    const migratedLang = this.languageService.getCurrentLanguageCode();
    return { ...DEFAULT_SETTINGS, languageCode: migratedLang };
  }

  private validate(settings: GlobalSettings): GlobalSettings {
    return {
      countryCode: settings.countryCode || DEFAULT_SETTINGS.countryCode,
      languageCode: settings.languageCode || DEFAULT_SETTINGS.languageCode,
      currencyCode: settings.currencyCode || DEFAULT_SETTINGS.currencyCode
    };
  }

  private persist(settings: GlobalSettings): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch (e) {
      console.error('Failed to save global settings to localStorage:', e);
    }
  }
}
